
/* ============================== src/components/Modals/DelegationModal.jsx ============================== */
// path: frontend/src/components/Modals/DelegationModal.jsx
/**
 * Единая модалка передачи полномочий: роли + задачи + проекты.
 */
import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { ModalShell } from "../ModalShell";
import { FormField } from "../FormField";
import { RecipientSelect } from "../DelegationFields";
import { useNestedModalEscape } from "../Templates/useNestedModalEscape";
import { useForm } from "../../hooks/useForm";
import { useAsyncSubmit } from "../../hooks/useAsyncSubmit";
import { api } from "../../api/client";
import { SUBSTITUTION_KIND_OPTIONS } from "../../utils/transferModes";
import { ROLES, isRoleDelegable } from "../../utils/constants";
import { TODAY, addDays, iso } from "../../utils/date";
import { useDelegationTasksQuery } from "../../api/queries/tasks";
import { useDelegationProjectsQuery } from "../../api/queries/projects";

const EMPTY_CANDIDATES = Object.freeze([]);
const EMPTY_TASKS = Object.freeze([]);
const EMPTY_PROJECTS = Object.freeze([]);

const DELEGABLE_TASK_STATUSES = new Set(["new", "inwork", "review"]);

const FIELDS = Object.freeze([
  "kind",
  "toId",
  "roles",
  "start",
  "end",
  "openEnd",
  "reasonText",
  "delegatedTaskIds",
  "delegatedProjectIds",
]);

function buildInitialValues() {
  return {
    kind: SUBSTITUTION_KIND_OPTIONS[0]?.value || "business_trip",
    toId: "",
    roles: [],
    start: TODAY,
    end: iso(addDays(new Date(), 14)),
    openEnd: false,
    reasonText: "",
    delegatedTaskIds: [],
    delegatedProjectIds: [],
  };
}

function CheckList({
  label,
  items,
  selected,
  onChange,
  disabled,
  renderLabel,
  header,
}) {
  const toggle = (id, checked) => {
    onChange(checked ? [...selected, id] : selected.filter((x) => x !== id));
  };
  return (
    <div className="field-row">
      <label className="field-label">{label}</label>
      <div className="flex-1">
        {header}
        <div className="sub-picks">
          {items.length === 0 && (
            <span className="mut sm">Нет доступных элементов</span>
          )}
          {items.map((item) => (
            <label className="dept-pick" key={item.id}>
              <input
                type="checkbox"
                checked={selected.includes(item.id)}
                disabled={disabled}
                onChange={(e) => toggle(item.id, e.target.checked)}
              />
              {renderLabel(item)}
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}

export function DelegationModal({ ur, onClose, onSubmit, toast }) {
  useNestedModalEscape(onClose);

  const { data: candidatesData } = useQuery({
    queryKey: ["role-delegations", "substitute-candidates"],
    queryFn: () => api.get("/role-delegations/substitute-candidates"),
    staleTime: 30_000,
  });
  const candidates = candidatesData ?? EMPTY_CANDIDATES;

  const { data: myTasksData } = useDelegationTasksQuery(ur.id);
  const myTasks = useMemo(() => {
    const raw = myTasksData ?? EMPTY_TASKS;
    return raw.filter((t) => DELEGABLE_TASK_STATUSES.has(t.status));
  }, [myTasksData]);

  const { data: myProjectsData } = useDelegationProjectsQuery(ur.id);
  const myProjects = useMemo(
    () => myProjectsData ?? EMPTY_PROJECTS,
    [myProjectsData],
  );

  const recipientOptions = useMemo(
    () =>
      candidates.map((e) => ({
        value: e.id,
        label: `${e.last} ${e.first}`,
      })),
    [candidates],
  );

  const initialValues = useMemo(() => buildInitialValues(), []);

  const validate = useCallback((values) => {
    const errs = {};
    if (!values.toId) errs.toId = "Выберите получателя";
    if (
      !values.roles.length &&
      !values.delegatedTaskIds.length &&
      !values.delegatedProjectIds.length
    ) {
      errs.roles = "Выберите хотя бы одну роль, задачу или проект";
    }
    if (!values.reasonText.trim()) errs.reasonText = "Укажите обоснование";
    if (!values.start) errs.start = "Дата начала обязательна";
    else if (values.start < TODAY)
      errs.start = "Дата начала не может быть в прошлом";
    if (!values.openEnd) {
      if (!values.end) errs.end = "Дата окончания обязательна";
      else if (values.end < values.start)
        errs.end = "Дата окончания не может быть раньше даты начала";
    }
    return errs;
  }, []);

  const { values, handleChange, handleSubmit, errors, touched } = useForm(
    initialValues,
    validate,
    { fields: FIELDS },
  );

  // Находим выбранного получателя и его роли
  const selectedRecipient = useMemo(
    () => candidates.find((c) => c.id === values.toId),
    [candidates, values.toId],
  );
  const recipientRoles = useMemo(
    () => new Set(selectedRecipient?.roles || []),
    [selectedRecipient],
  );

  // Фильтруем роли, которые уже есть у получателя
  const roleOptions = useMemo(
    () =>
      ur.roles
        .filter(isRoleDelegable)
        .filter((r) => !recipientRoles.has(r))
        .map((r) => ({
          value: r,
          label: ROLES[r]?.label || r,
        })),
    [ur.roles, recipientRoles],
  );

  // При смене получателя вычищаем из выбранных ролей те, которые у него уже есть
  const handleRecipientChange = useCallback(
    (toId) => {
      handleChange("toId", toId);
      const newRecipient = candidates.find((c) => c.id === toId);
      const newRecipientRoles = new Set(newRecipient?.roles || []);
      const validRoles = values.roles.filter((r) => !newRecipientRoles.has(r));
      if (validRoles.length !== values.roles.length) {
        handleChange("roles", validRoles);
      }
    },
    [candidates, values.roles, handleChange],
  );

  // Быстрый выбор задач по статусам
  const allTaskIds = useMemo(() => myTasks.map((t) => t.id), [myTasks]);
  const inworkTaskIds = useMemo(
    () => myTasks.filter((t) => t.status === "inwork").map((t) => t.id),
    [myTasks],
  );
  const reviewTaskIds = useMemo(
    () => myTasks.filter((t) => t.status === "review").map((t) => t.id),
    [myTasks],
  );

  const isAllTasksSelected =
    allTaskIds.length > 0 &&
    allTaskIds.every((id) => values.delegatedTaskIds.includes(id));
  const isInworkSelected =
    inworkTaskIds.length > 0 &&
    inworkTaskIds.every((id) => values.delegatedTaskIds.includes(id));
  const isReviewSelected =
    reviewTaskIds.length > 0 &&
    reviewTaskIds.every((id) => values.delegatedTaskIds.includes(id));

  const saveAsync = useCallback(
    async (v) => {
      await onSubmit({
        kind: v.kind,
        fromId: ur.id,
        toId: v.toId,
        start: v.start,
        end: v.openEnd ? null : v.end,
        reasonText: v.reasonText.trim(),
        roles: [...v.roles],
        kbIds: [...(ur.kbIds || [])],
        headDeptIds: [...(ur.headDeptIds || [])],
        delegatedTaskIds: [...v.delegatedTaskIds],
        delegatedProjectIds: [...v.delegatedProjectIds],
        delegateTasks:
          v.delegatedTaskIds.length > 0 || v.delegatedProjectIds.length > 0,
        taskStatuses: [],
      });
      onClose();
    },
    [ur, onSubmit, onClose],
  );

  const { submit, isSubmitting } = useAsyncSubmit(saveAsync, (err) => {
    toast(err.message || "Не удалось создать запись", "error");
  });

  const hasCandidates = candidates.length > 0;
  const canSubmit = hasCandidates && !isSubmitting;

  return (
    <ModalShell
      title="Передача полномочий"
      onClose={onClose}
      onSave={handleSubmit(submit)}
      saveLabel="Отправить на согласование"
      width={720}
      saveDisabled={!canSubmit}
    >
      <p className="mut sm">
        Получатель вступает в полномочия после согласования. Роли, задачи и
        проекты передаются одновременно; при завершении замещения задачи и
        проекты вернутся, только если они всё ещё у получателя.
      </p>

      {!hasCandidates && (
        <div className="info-box">
          Нет сотрудников, доступных для получения полномочий. Уточните состав
          подразделения у администратора или HR.
        </div>
      )}

      <div className="project-info-fields">
        <FormField
          label="Причина"
          required
          type="select"
          options={SUBSTITUTION_KIND_OPTIONS}
          value={values.kind}
          onChange={(v) => handleChange("kind", v)}
          disabled={isSubmitting}
        />

        <RecipientSelect
          value={values.toId}
          onChange={handleRecipientChange}
          options={recipientOptions}
          error={touched.toId && errors.toId}
          disabled={isSubmitting || !hasCandidates}
        />

        <FormField
          label="Передаваемые роли"
          type="select"
          multiple
          placement="top"
          options={roleOptions}
          value={values.roles}
          onChange={(v) => handleChange("roles", v)}
          error={touched.roles && errors.roles}
          disabled={isSubmitting || roleOptions.length === 0}
          below={
            roleOptions.length === 0 && (
              <div className="mut sm mt-1">
                {ur.roles.filter(isRoleDelegable).length > 0
                  ? "У выбранного получателя уже есть все ваши роли, доступные для передачи."
                  : "У вас нет ролей, которые можно передать."}
              </div>
            )
          }
        />

        <div className="fields-row fields-row--stacked">
          <FormField
            label="Дата начала"
            required
            type="date"
            inline
            value={values.start}
            onChange={(v) => handleChange("start", v)}
            error={touched.start && errors.start}
            disabled={isSubmitting}
          />
          <FormField
            label="Дата окончания"
            type="date"
            inline
            value={values.end}
            onChange={(v) => handleChange("end", v)}
            error={touched.end && errors.end}
            disabled={isSubmitting || values.openEnd}
            below={
              <label className="dept-pick mt-2">
                <input
                  type="checkbox"
                  checked={values.openEnd}
                  disabled={isSubmitting}
                  onChange={(e) => handleChange("openEnd", e.target.checked)}
                />
                до отмены
              </label>
            }
          />
        </div>

        <FormField
          label="Пояснение"
          required
          type="textarea"
          rows={2}
          value={values.reasonText}
          onChange={(v) => handleChange("reasonText", v)}
          error={touched.reasonText && errors.reasonText}
          placeholder="Например: командировка в филиал, отпуск, обучение, ротация…"
        />

        <CheckList
          label="Передать задачи (на мне)"
          items={myTasks}
          selected={values.delegatedTaskIds}
          onChange={(v) => handleChange("delegatedTaskIds", v)}
          disabled={isSubmitting}
          renderLabel={(t) =>
            `${t.title}${t.deadline ? ` — до ${t.deadline}` : ""}`
          }
          header={
            myTasks.length > 0 && (
              <div className="flex gap-2 mb-2 flex-wrap">
                <label className="dept-pick">
                  <input
                    type="checkbox"
                    checked={isAllTasksSelected}
                    disabled={isSubmitting}
                    onChange={(e) => {
                      if (e.target.checked)
                        handleChange("delegatedTaskIds", allTaskIds);
                      else handleChange("delegatedTaskIds", []);
                    }}
                  />
                  Все задачи
                </label>
                <label className="dept-pick">
                  <input
                    type="checkbox"
                    checked={isInworkSelected}
                    disabled={isSubmitting || inworkTaskIds.length === 0}
                    onChange={(e) => {
                      if (e.target.checked) {
                        const newIds = new Set([
                          ...values.delegatedTaskIds,
                          ...inworkTaskIds,
                        ]);
                        handleChange("delegatedTaskIds", Array.from(newIds));
                      } else {
                        handleChange(
                          "delegatedTaskIds",
                          values.delegatedTaskIds.filter(
                            (id) => !inworkTaskIds.includes(id),
                          ),
                        );
                      }
                    }}
                  />
                  В работе
                </label>
                <label className="dept-pick">
                  <input
                    type="checkbox"
                    checked={isReviewSelected}
                    disabled={isSubmitting || reviewTaskIds.length === 0}
                    onChange={(e) => {
                      if (e.target.checked) {
                        const newIds = new Set([
                          ...values.delegatedTaskIds,
                          ...reviewTaskIds,
                        ]);
                        handleChange("delegatedTaskIds", Array.from(newIds));
                      } else {
                        handleChange(
                          "delegatedTaskIds",
                          values.delegatedTaskIds.filter(
                            (id) => !reviewTaskIds.includes(id),
                          ),
                        );
                      }
                    }}
                  />
                  На проверке
                </label>
              </div>
            )
          }
        />

        <CheckList
          label="Передать проекты (под моим управлением)"
          items={myProjects}
          selected={values.delegatedProjectIds}
          onChange={(v) => handleChange("delegatedProjectIds", v)}
          disabled={isSubmitting}
          renderLabel={(p) => `${p.code} — ${p.name}`}
        />

        <p className="mut sm">
          Пустые списки = передаются только роли. Задачи и проекты возвращаются
          инициатору при завершении замещения, если не были переназначены.
        </p>
      </div>
    </ModalShell>
  );
}

export default DelegationModal;
